import type { ApiError, MeResponse } from '@eventping/shared'
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

export class ApiFailure extends Error {
  override name = 'ApiFailure'
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly field?: string,
  ) {
    super(message)
  }
}

/** JSON in, JSON out, same origin. Errors carry the server's friendly message. */
export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
    headers: init.body === undefined ? {} : { 'content-type': 'application/json' },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  })
  const data = (await res.json().catch(() => null)) as unknown
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiError>
    throw new ApiFailure(
      res.status,
      err.error ?? 'error',
      err.message ?? 'Something went wrong. Try again.',
      err.field,
    )
  }
  return data as T
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => !(error instanceof ApiFailure && error.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
})

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/me'),
    staleTime: 60_000,
  })
}

/**
 * Whether texts are on (MeResponse.textsEnabled). False in texts-off mode, and until /api/me
 * answers, so the email-only product never shows a texting control even for a moment.
 */
export function useTexts(): boolean {
  return useMe().data?.textsEnabled ?? false
}

/** How often a page polls while its live stream is down. */
export const POLL_MS = 10_000

/**
 * Live updates: a server-sent events stream that says "something changed"; the matching
 * query refetches. EventSource reconnects by itself after a drop. While the stream is not up
 * (connecting, dropped, or a proxy holding events back) the query is polled instead, so the
 * page still catches up.
 */
export function useLive(url: string | null, queryKey: readonly unknown[]) {
  const client = useQueryClient()
  const key = JSON.stringify(queryKey)
  const [connected, setConnected] = useState(false)
  useEffect(() => {
    if (!url) return
    const source = new EventSource(url)
    const refetch = () => client.invalidateQueries({ queryKey: JSON.parse(key) })
    source.addEventListener('changed', refetch)
    // After a reconnect, catch up on anything missed while away.
    source.addEventListener('ready', () => {
      setConnected(true)
      void refetch()
    })
    source.addEventListener('error', () => setConnected(false))
    return () => {
      source.close()
      setConnected(false)
    }
  }, [url, key, client])
  useEffect(() => {
    if (!url || connected) return
    const timer = setInterval(() => {
      void client.invalidateQueries({ queryKey: JSON.parse(key) })
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [url, key, client, connected])
}

export async function signOut(scope: 'local' | 'all' = 'local') {
  const res = await api<{ redirect: string }>(`/auth/logout?scope=${scope}`, { method: 'POST' })
  window.location.assign(res.redirect)
}
